// @flow

import React, { useEffect, useState, type Node } from 'react';
import { type IncomingPayment } from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { type AppValues } from '@tectonics/app-values';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { FallbackErrorBoundary } from '@tectonics/fallback-error-boundary';
import { LoadingPage } from '@tectonics/components';
import { type ControllerContextType, ControllerContextProvider, useControllerContext } from './context';
import { useFetchIncomingPayment } from './use-fetch-incoming-payment';
import { useEbanxJsInitialization } from './use-ebanx-js-initialization';
import { getLookAndFeelFromEnvironment } from './get-look-and-feel-from-environment';
import { useDynamicPaymentSpec } from './payment-spec-generator';
import { useDynamicLookAndFeel } from './use-dynamic-look-and-feel';
import { PaymentRender } from './payment-render';
import { getPaymentBasedInitialUserInputValues } from './get-payment-based-initial-user-input-values';
import { LanguageContextProvider } from '@tectonics/controller/language/LanguageContext';

export function Controller(props: ControllerContextType): Node {
  return (
    <FallbackErrorBoundary>
      <ControllerContextProvider value={props}>
        <LanguageContextProvider>
          <ControllerRender />
        </LanguageContextProvider>
      </ControllerContextProvider>
    </FallbackErrorBoundary>
  );
}

function ControllerRender(): Node {
  const [incomingPayment, setIncomingPayment] = useState<?IncomingPayment>(null);

  const [fetchingIncomingPayment, fetchingIncomingPaymentError, requestReloadContext] = useFetchIncomingPayment(setIncomingPayment);

  useEbanxJsInitialization(incomingPayment);

  if (!fetchingIncomingPayment && !incomingPayment) {
    if (fetchingIncomingPaymentError) {
      throw fetchingIncomingPaymentError;
    } else {
      throw new Error('Controller: No incoming payment was loaded.');
    }
  }

  if (incomingPayment) {
    return <UserInputValuesWrapperRender
      incomingPayment={incomingPayment}
      setIncomingPayment={setIncomingPayment}
      requestReloadContext={requestReloadContext}
    />;
  }

  return null;
}

type UserInputValuesWrapperRenderProps = {|
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: IncomingPayment => void,
  +requestReloadContext: () => void,
|};

function UserInputValuesWrapperRender({
  incomingPayment,
  setIncomingPayment,
  requestReloadContext,
}: UserInputValuesWrapperRenderProps): Node {
  const [appValues, setAppValues] = useState<AppValues>({});
  const [userInputValues, setUserInputValues] = useState<?UserInputValues>(null);

  useSetInitialUserInputValues({
    incomingPayment,
    setUserInputValues,
  });

  return (
    <SafeControllerRender
      incomingPayment={incomingPayment}
      setIncomingPayment={setIncomingPayment}
      appValues={appValues}
      setAppValues={setAppValues}
      userInputValues={userInputValues}
      setUserInputValues={setUserInputValues}
      requestReloadContext={requestReloadContext}
    />
  );
}

type SafeControllerRenderProps = {|
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: IncomingPayment => void,
  +appValues: AppValues,
  +setAppValues: AppValues => void,
  +userInputValues: ?UserInputValues,
  +setUserInputValues: UserInputValues => void,
  +requestReloadContext: () => void,
|};

function SafeControllerRender({
  incomingPayment,
  setIncomingPayment,
  appValues,
  setAppValues,
  userInputValues,
  setUserInputValues,
  requestReloadContext,
}: SafeControllerRenderProps): Node {
  const { lookAndFeelCode, localTenancy } = useControllerContext();

  const [lookAndFeel, setLookAndFeel] = useState<LookAndFeel>(
    getLookAndFeelFromEnvironment(incomingPayment, lookAndFeelCode)
  );
  const [paymentSpec, loadingPaymentSpecError] = useDynamicPaymentSpec(incomingPayment, localTenancy);
  const [isLoadingLookAndFeel, LookAndFeelLoadingPage, lookAndFeelRoot] = useDynamicLookAndFeel(lookAndFeel);

  if (loadingPaymentSpecError) {
    throw loadingPaymentSpecError;
  }

  if (isLoadingLookAndFeel || !paymentSpec || !userInputValues) {
    if (LookAndFeelLoadingPage) {
      return <LookAndFeelLoadingPage />;
    } else {
      return <LoadingPage />;
    }
  }

  if (!lookAndFeelRoot) throw new Error('Controller: There is no look and feel loaded for the incoming payment');

  return (
    <FallbackErrorBoundary incomingPayment={incomingPayment}>
      <PaymentRender
        incomingPayment={incomingPayment}
        setIncomingPayment={setIncomingPayment}
        spec={paymentSpec}
        lookAndFeel={lookAndFeel}
        setLookAndFeel={setLookAndFeel}
        lookAndFeelLoadingPage={LookAndFeelLoadingPage}
        lookAndFeelRootPage={lookAndFeelRoot}
        appValues={appValues}
        setAppValues={setAppValues}
        userInputValues={userInputValues}
        setUserInputValues={setUserInputValues}
        requestReloadContext={requestReloadContext}
      />
    </FallbackErrorBoundary>
  );
}

type UseSetInitialUserInputValuesProps = {|
  +incomingPayment: ?IncomingPayment,
  +setUserInputValues: UserInputValues => void,
|};

function useSetInitialUserInputValues({ incomingPayment, setUserInputValues }: UseSetInitialUserInputValuesProps) {
  useEffect(() => {
    if (incomingPayment) {
      setUserInputValues(getPaymentBasedInitialUserInputValues(incomingPayment));
    }
  }, [!!incomingPayment]);
}
